import { useState, useEffect, useCallback } from 'react';
import { Bell, AlertTriangle, AlertCircle, CheckCircle2, Info, RefreshCw } from 'lucide-react';
import { api } from '../../../api/client';

export function NotificationsTab({ t }: { t: (k: string) => string }) {
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api.get<any>('/notifications');
    if (res.success) {
      setNotifications((res as any).notifications || []);
      setUnread((res as any).unread || 0);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const markAllRead = async () => {
    await api.post('/notifications/read-all', {});
    load();
  };

  const markRead = async (id: number) => {
    await api.post(`/notifications/${id}/read`, {});
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setUnread(prev => Math.max(0, prev - 1));
  };

  const filtered = notifications.filter(n => {
    if (filter === 'unread') return !n.read;
    if (filter === 'all') return true;
    return n.type === filter;
  });

  const typeIcon = (type: string) => {
    switch (type) {
      case 'warning': return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'error': return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'success': return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      default: return <Info className="w-4 h-4 text-blue-500" />;
    }
  };

  const timeAgo = (date: string) => {
    const diff = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-[var(--color-text)]">{t('settings.notifHistory')}</h2>
        <div className="flex items-center gap-2">
          {unread > 0 && (
            <button onClick={markAllRead}
              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90">
              {t('settings.notifMarkAllRead')} ({unread})
            </button>
          )}
          <button onClick={load} className="p-1.5 rounded hover:bg-[var(--color-surface-hover)] text-[var(--color-text-muted)]">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {[
          { key: 'all', label: t('settings.notifAll') },
          { key: 'unread', label: t('settings.notifUnread') },
          { key: 'info', label: t('settings.notifInfo') },
          { key: 'warning', label: t('settings.notifWarning') },
          { key: 'error', label: t('settings.notifError') },
          { key: 'success', label: t('settings.notifSuccess') },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
              filter === f.key
                ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)] text-[var(--color-primary)]'
                : 'border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)]'
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-8 text-[var(--color-text-muted)]">
          <RefreshCw className="w-5 h-5 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-[var(--color-text-muted)]">
          <Bell size={40} className="mx-auto mb-3 opacity-20" />
          <p>{t('settings.notifEmpty')}</p>
        </div>
      ) : (
        <div className="divide-y divide-[var(--color-border)] border border-[var(--color-border)] rounded-lg overflow-hidden">
          {filtered.map((n: any) => (
            <div key={n.id}
              onClick={() => !n.read && markRead(n.id)}
              className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-[var(--color-surface-hover)] ${
                !n.read ? 'bg-[var(--color-primary-light)]/30' : ''
              }`}>
              <div className="mt-0.5">{typeIcon(n.type)}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-medium ${!n.read ? 'text-[var(--color-text)]' : 'text-[var(--color-text-secondary)]'}`}>
                    {n.title}
                  </span>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-[var(--color-primary)]" />}
                </div>
                {n.message && (
                  <p className="text-xs text-[var(--color-text-muted)] mt-0.5 line-clamp-2">{n.message}</p>
                )}
              </div>
              <span className="text-xs text-[var(--color-text-muted)] shrink-0">{timeAgo(n.created_at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
